import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import "./style.css";
import NoraLogo from '../../images/nora-ai-logo.png';
import CheckCircle from "../../images/checkCircleNora.png";

function CandidateApplicationScreen() {
  const { companyId, companyInterviewId } = useParams();
  const [positionDetails, setPositionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [finished, setFinished] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location: "",
    currentRole: "",
    linkedIn: "",
    resume: null,
  });
  const [countryCode, setCountryCode] = useState("+1");
  const [phoneError, setPhoneError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);

  const getPositionDetailsEndpoint = `${process.env.REACT_APP_GET_POSITION_DETAILS}`;
  const getInterviewLinkEndpoint = `${process.env.REACT_APP_GET_INTERVIEW_LINK}`;

  useEffect(() => {
    const fetchPositionDetails = async () => {
      try {
        const response = await fetch(
          `${getPositionDetailsEndpoint}?companyInterviewId=${companyInterviewId}&companyId=${companyId}`
        );

        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.Message || "Failed to fetch position details.");
          setLoading(false);
          return;
        }

        const data = await response.json();
        setPositionDetails(data);
        setLoading(false);
      } catch (error) {
        setError("An error occurred while fetching position details.");
        setLoading(false);
      }
    };

    fetchPositionDetails();
  }, [companyId, companyInterviewId]);

  // Phone number validation
  const validatePhone = (phone) => {
    if (!phone) return false;
    
    // Simple validation based on length
    const digitsOnly = phone.replace(/\D/g, '');
    
    // For US/Canada (+1), require exactly 10 digits
    if (countryCode === "+1" && digitsOnly.length !== 10) {
      setPhoneError("US/Canada numbers must be 10 digits");
      return false;
    }
    
    // For international, require at least 7 digits
    if (digitsOnly.length < 7) {
      setPhoneError("Phone number is too short");
      return false;
    }
    
    // For international, limit to 15 digits (E.164 standard)
    if (digitsOnly.length > 15) {
      setPhoneError("Phone number is too long");
      return false;
    }
    
    setPhoneError("");
    return true;
  };

  // Form validation
  useEffect(() => {
    // Check if required fields are filled to validate the form
    const { name, email, phone, location, resume } = formData;
    const phoneValid = validatePhone(phone);
    
    const formValid =
      name.trim() !== "" &&
      email.trim() !== "" &&
      phone.trim() !== "" &&
      phoneValid &&
      location.trim() !== "" &&
      resume !== null;
      
    setIsFormValid(formValid);
  }, [formData, countryCode]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Phone field change handler
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits, spaces, dashes, parentheses
    const sanitized = value.replace(/[^\d\s\-()]/g, '');
    
    setFormData(prev => ({
      ...prev,
      phone: sanitized
    }));
    
    validatePhone(sanitized);
  };

  // Country code change handler
  const handleCountryCodeChange = (e) => {
    setCountryCode(e.target.value);
    // Revalidate phone with new country code
    validatePhone(formData.phone);
  };

  // Handle file changes
  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      resume: e.target.files[0],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate phone one more time
    if (!validatePhone(formData.phone)) {
      return; // Don't submit if phone is invalid
    }

    // Format phone number to E.164 before submission
    const digitsOnly = formData.phone.replace(/\D/g, '');
    const formattedPhone = `${countryCode}${digitsOnly}`;
    
    // Prepare form data for API
    const payload = new FormData();
    payload.append("companyInterviewId", companyInterviewId);
    payload.append("Name", formData.name);
    payload.append("Email", formData.email);
    payload.append("Phone", formattedPhone); // Use formatted E.164 phone
    payload.append("Location", formData.location);
    payload.append("CurrentRole", formData.currentRole || ""); // Optional field
    payload.append("LinkedIn", formData.linkedIn || ""); // Optional field
    if (formData.resume) {
      payload.append("file", formData.resume);
    }

    try {
      const response = await fetch(getInterviewLinkEndpoint, {
        method: "POST",
        body: payload,
      });

      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.Message || "Failed to submit the application.");
        return;
      }

      const data = await response.json();

      // Check the API response for "already applied"
      if (data.api_return_string === "already applied!") {
        alert("You've already applied to this position!");
      } else {
        alert("Application submitted successfully!");
        setFinished(true);
      }

    } catch (error) {
      alert("An error occurred while submitting your application.");
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="candidate-application-container">
      <div className="candidate-application-header">
        <img 
          src={positionDetails?.companyLogo || NoraLogo} 
          alt={positionDetails?.companyName || "Nora AI"} 
          className="candidate-application-logo"
          style={{
            height: positionDetails?.companyLogo ? '60px' : 'auto',
            width: positionDetails?.companyLogo ? 'auto' : '150px',
            objectFit: 'contain',
          }}
        />
      </div>

      {positionDetails?.active === false ? (
        <div className="position-title" style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          marginTop: '15%'
        }}>
          <h1>Page is no longer available</h1>
          <p>The job you are looking for may have been closed or removed.</p>
        </div>
      ) : (
        finished ? (
          <div className="completionScreen-overlay">
            <div className="completionScreen-container">
              <div className="completionScreen-content">
                <img
                  src={CheckCircle}
                  alt="Completion Check"
                  className="completion-icon"
                />
                <h2 className="completionScreen-title">Your application has been submitted</h2>
                <p className="completionScreen-subtitle">
                  We will get back to you ASAP. You may now close this browser window.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="candidate-application-content">

            <div className="position-title" style={{textAlign: 'center'}}>
              <h1>{positionDetails?.position}</h1>
              <p>{positionDetails?.companyName}, {positionDetails?.positionLocation} | {positionDetails?.positionType}</p>
            </div>

            {!showForm ? (
              <>
                <h2 className="section-title3">About the Position</h2>
                <div
                  className="job-description"
                  dangerouslySetInnerHTML={{
                    __html: positionDetails?.jobDescription,
                  }}
                />
                <button className="apply-button3" onClick={() => setShowForm(true)}>
                  Apply
                </button>
              </>
            ) : (
              <form className="application-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <label>
                    Full Name *
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Full Name"
                      required
                      value={formData.name}
                      onChange={handleFormChange}
                    />
                  </label>
                  <label>
                    Email *
                    <input
                      type="email"
                      name="email"
                      placeholder="youremail@email.com"
                      required
                      value={formData.email}
                      onChange={handleFormChange}
                    />
                  </label>
                  <label>
                    Phone Number *
                    <div className="phone-input-container">
                      <select 
                        value={countryCode}
                        onChange={handleCountryCodeChange}
                        className="country-code-select"
                      >
                        <option value="+1">+1 (US/Canada)</option>
                        <option value="+54">+54 (Argentina)</option>
                        <option value="+880">+880 (Bangladesh)</option>
                        <option value="+55">+55 (Brazil)</option>
                        <option value="+56">+56 (Chile)</option>
                        <option value="+86">+86 (China)</option>
                        <option value="+57">+57 (Colombia)</option>
                        <option value="+33">+33 (France)</option>
                        <option value="+49">+49 (Germany)</option>
                        <option value="+91">+91 (India)</option>
                        <option value="+81">+81 (Japan)</option>
                        <option value="+230">+230 (Mauritius)</option>
                        <option value="+52">+52 (Mexico)</option>
                        <option value="+234">+234 (Nigeria)</option>
                        <option value="+92">+92 (Pakistan)</option>
                        <option value="+63">+63 (Philippines)</option>
                        <option value="+971">+971 (UAE)</option>
                        <option value="+44">+44 (UK)</option>
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="(123) 456-7890"
                        required
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        className={phoneError ? "phone-input-error" : ""}
                      />
                    </div>
                    {phoneError && (
                      <small className="phone-error-message">{phoneError}</small>
                    )}
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Location *
                    <input
                      type="text"
                      name="location"
                      placeholder="e.g. San Diego, CA"
                      required
                      value={formData.location}
                      onChange={handleFormChange}
                    />
                  </label>
                  <label>
                    Current Role
                    <input
                      type="text"
                      name="currentRole"
                      placeholder="e.g. Software Engineer I"
                      value={formData.currentRole}
                      onChange={handleFormChange}
                    />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    LinkedIn
                    <input
                      type="url"
                      name="linkedIn"
                      placeholder="Your LinkedIn URL"
                      value={formData.linkedIn}
                      onChange={handleFormChange}
                    />
                  </label>

                  <label>
                    Upload Resume *
                    <input
                      type="file"
                      required
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
                <div className="form-buttons2">
                  <button
                    type="button"
                    className="back-button2"
                    onClick={() => setShowForm(false)}
                  >
                    Back
                  </button>
                  <button
                    type="submit"
                    className="submit-button2"
                    disabled={!isFormValid} 
                    style={{
                      backgroundColor: isFormValid ? "#7D994D" : "#cccccc",
                      cursor: isFormValid ? "pointer" : "not-allowed",
                    }}
                  >
                    Submit
                  </button>
                </div>
              </form>
            )}
          </div>
        )
      )}
    </div>
  );
}

export default CandidateApplicationScreen;